import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { type QuoteUnit, defaultUnit } from './quoteUtils';

export type { QuoteUnit };
export { defaultUnit };

export interface QuoteItem {
  id: string;
  title: string;
  sku?: string;
  brand: string;
  image?: string;
  category?: string;
  quantity: number;
  unit: QuoteUnit;
}

interface QuoteContextValue {
  items: QuoteItem[];
  toggle: (item: Omit<QuoteItem, 'quantity' | 'unit'>) => void;
  remove: (id: string) => void;
  updateQuantity: (id: string, qty: number) => void;
  has: (id: string) => boolean;
  clear: () => void;
  count: number;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

const QuoteContext = createContext<QuoteContextValue | null>(null);

export function QuoteProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<QuoteItem[]>(() => {
    try {
      const saved = localStorage.getItem('mayi_quote_items');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('mayi_quote_items', JSON.stringify(items));
  }, [items]);

  const toggle = useCallback((item: Omit<QuoteItem, 'quantity' | 'unit'>) => {
    setItems(prev =>
      prev.some(i => i.id === item.id)
        ? prev.filter(i => i.id !== item.id)
        : [...prev, { ...item, quantity: 1, unit: defaultUnit(item.brand, item.category) }]
    );
  }, []);

  const remove = useCallback((id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string, qty: number) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, quantity: Math.max(0, qty) } : i));
  }, []);

  const has = useCallback((id: string) => items.some(i => i.id === id), [items]);

  const clear = useCallback(() => setItems([]), []);

  return (
    <QuoteContext.Provider value={{
      items, toggle, remove, updateQuantity, has, clear,
      count: items.length,
      isDrawerOpen,
      openDrawer: () => setIsDrawerOpen(true),
      closeDrawer: () => setIsDrawerOpen(false),
    }}>
      {children}
    </QuoteContext.Provider>
  );
}

export function useQuote() {
  const ctx = useContext(QuoteContext);
  if (!ctx) throw new Error('useQuote must be used within QuoteProvider');
  return ctx;
}
